import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
  styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import AnimatedSection from '../components/AnimatedSection';
import '../glow-effect.css';

// Configuración de EmailJS desde variables de entorno
const EMAILJS_CONFIG = {
  PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '',
  SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
  TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
  TO_EMAIL: process.env.REACT_APP_EMAILJS_TO_EMAIL || 'contact@peoplefortech.com',
};

// Debug: Verificar que las variables se carguen (solo en desarrollo)
if (process.env.NODE_ENV === 'development') {
  console.log('EmailJS Config:', {
    PUBLIC_KEY: EMAILJS_CONFIG.PUBLIC_KEY ? '✓ Configurado' : '✗ Falta',
    SERVICE_ID: EMAILJS_CONFIG.SERVICE_ID ? '✓ Configurado' : '✗ Falta',
    TEMPLATE_ID: EMAILJS_CONFIG.TEMPLATE_ID ? '✓ Configurado' : '✗ Falta',
  });
}

// Mover los componentes styled fuera del componente para evitar re-renders
const SectionContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  backgroundColor: 'transparent',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  color: '#000000',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  color: '#666666',
}));

// Styled component para los círculos de servicios
const ServiceCircle = styled(Box)(({ theme, selected }) => ({
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  backgroundColor: selected ? '#000000' : '#f5f5f5',
  border: selected ? '2px solid #000000' : '2px solid #e0e0e0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: selected ? '0 4px 12px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  zIndex: 0,
  '&::before': {
    content: '""',
    background: 'linear-gradient(45deg, #ff0000, #ff7300, #fffb00, #48ff00, #00ffd5, #002bff, #7a00ff, #ff00c8, #ff0000)',
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    backgroundSize: '400%',
    zIndex: -1,
    filter: 'blur(5px)',
    width: 'calc(100% + 4px)',
    height: 'calc(100% + 4px)',
    animation: 'glowing 20s linear infinite',
    opacity: 0,
    transition: 'opacity .3s ease-in-out',
    borderRadius: '50%',
  },
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    '&::before': {
      opacity: 1,
    },
  },
  [theme.breakpoints.down('sm')]: {
    width: '85px',
    height: '85px',
  },
}));

const ServiceText = styled(Typography)(({ selected }) => ({
  color: selected ? '#ffffff' : '#000000',
  fontWeight: 'bold',
  fontSize: '0.55rem',
  textAlign: 'center',
  textTransform: 'uppercase',
  fontFamily: '"Montserrat", sans-serif',
  padding: '4px 6px',
  lineHeight: 1.3,
  width: '100%',
  margin: 0,
  wordBreak: 'break-word',
  hyphens: 'auto',
  display: 'block',
}));

const ContactCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  padding: theme.spacing(5),
  width: '100%',
  maxWidth: '600px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const ContactSection = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [selectedService, setSelectedService] = useState('consultory');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    { key: 'consultory', titleKey: 'services.consultory.title' },
    { key: 'development', titleKey: 'services.development.title' },
    { key: 'seo', titleKey: 'services.seo.title' },
    { key: 'other', titleKey: 'contact.other' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validar que las credenciales estén configuradas
    if (!EMAILJS_CONFIG.PUBLIC_KEY || EMAILJS_CONFIG.PUBLIC_KEY === 'TU_PUBLIC_KEY_AQUI' || !EMAILJS_CONFIG.PUBLIC_KEY.trim()) {
      console.error('EmailJS Public Key no está configurado. Por favor, configura REACT_APP_EMAILJS_PUBLIC_KEY en el archivo .env');
      setMessage({ type: 'error', text: 'Error de configuración: falta Public Key. Por favor contacta al administrador.' });
      return;
    }

    if (!EMAILJS_CONFIG.SERVICE_ID || EMAILJS_CONFIG.SERVICE_ID === 'TU_SERVICE_ID_AQUI' || !EMAILJS_CONFIG.SERVICE_ID.trim()) {
      console.error('EmailJS Service ID no está configurado. Por favor, configura REACT_APP_EMAILJS_SERVICE_ID en el archivo .env');
      setMessage({ type: 'error', text: 'Error de configuración: falta Service ID. Por favor contacta al administrador.' });
      return;
    }

    if (!EMAILJS_CONFIG.TEMPLATE_ID || EMAILJS_CONFIG.TEMPLATE_ID === 'TU_TEMPLATE_ID_AQUI' || !EMAILJS_CONFIG.TEMPLATE_ID.trim()) {
      console.error('EmailJS Template ID no está configurado. Por favor, configura REACT_APP_EMAILJS_TEMPLATE_ID en el archivo .env');
      setMessage({ type: 'error', text: 'Error de configuración: falta Template ID. Por favor contacta al administrador.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const serviceName = selectedService 
        ? services.find(s => s.key === selectedService)?.titleKey 
          ? t(services.find(s => s.key === selectedService).titleKey)
          : selectedService
        : 'No especificado';
      
      // Preparar los parámetros del template
      const templateParams = {
        to_email: EMAILJS_CONFIG.TO_EMAIL,
        from_email: email,
        service: serviceName,
        message: `Nuevo contacto desde el formulario web.\n\nEmail del contacto: ${email}\nServicio de interés: ${serviceName}`,
        subject: 'Contacto desde web - PeopleForTech',
      };

      // Inicializar EmailJS con tu Public Key
      emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);

      // Enviar el email
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams
      );
      
      setMessage({ type: 'success', text: t('contact.success') });
      setEmail('');
      setSelectedService('consultory'); // Resetear a consultoría por defecto
      
      // Limpiar mensaje después de 5 segundos
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
    } catch (error) {
      console.error('Error al enviar email:', error);
      setMessage({ type: 'error', text: t('contact.error') || 'Error al enviar el mensaje. Por favor intenta de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedSection direction="bottom" delay={0.3}>
      <SectionContainer component="section">
        <Stack spacing={3} alignItems="center">
          <ContactCard>
            <Box sx={{ mb: 3 }}>
              <Title variant="h3">{t('contact.title')}</Title>
              <Subtitle variant="body1">{t('contact.subtitle')}</Subtitle>
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
          <TextField
            fullWidth
            label={t('contact.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('contact.emailPlaceholder')}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'black',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'black',
                },
              },
            }}
          />

          {/* Selector de servicios con círculos */}
          <Box sx={{ width: '100%' }}>
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: '#666666',
                textAlign: 'center',
                fontSize: '0.9rem',
              }}
            >
              {t('contact.selectInterest')}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ gap: 2 }}
            >
              {services.map((service) => (
                <ServiceCircle
                  key={service.key}
                  selected={selectedService === service.key}
                  onClick={() => setSelectedService(service.key)}
                >
                  <ServiceText selected={selectedService === service.key}>
                    {t(service.titleKey)}
                  </ServiceText>
                </ServiceCircle>
              ))}
            </Stack>
          </Box>

          {message.text && (
            <Alert severity={message.type === 'success' ? 'success' : 'error'}>
              {message.text}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isSubmitting}
            className="glow-on-hover"
            sx={{
              py: 1.5,
              backgroundColor: '#000000',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: 10,
              '&:hover': {
                backgroundColor: '#333333',
              },
              '&:disabled': {
                backgroundColor: '#666666',
              },
            }}
          >
            {isSubmitting ? (t('contact.sending') || 'Enviando...') : t('contact.submit')}
          </Button>
        </Box>
          </ContactCard>
      </Stack>
    </SectionContainer>
    </AnimatedSection>
  );
};

export default ContactSection;

